/** Explain why a game is worth opening, using current scores and observed public-room plays. */
import { formatClock, type Fan, type GameEvent, type LiveGame, type LiveGameContext } from '@clutch/core';
import { gameStory, type GameStory } from '../stage/story';
import { liveCard, type FeedCard } from './feed';
import { gameElapsed } from '../stage/momentum';

export type DiscoveryGame = {
  card: FeedCard;
  followed: boolean;
  headline: string;
  detail: string;
  label: string;
  rank: number;
  context?: LiveGameContext;
  story?: GameStory;
  player?: GameEvent;
};

function clock(detail: string): { period: number; sec: number } | undefined {
  if (/halftime/i.test(detail)) return { period: 2, sec: 0 };
  const m = /\b(Q([1-4])|(?:(\d+)OT|OT))\s+(\d+):(\d+)/i.exec(detail);
  return m ? { period: m[2] ? Number(m[2]) : 4 + Number(m[3] ?? 1), sec: Number(m[4]) * 60 + Number(m[5]) } : undefined;
}

function freshContext(game: LiveGame, context: LiveGameContext | undefined, now: number): LiveGameContext | undefined {
  if (!context || context.gameId !== game.gameId || context.state.status !== 'live' || !context.feed.healthy || context.freshness !== 'current') return undefined;
  const polled = Date.parse(context.feed.lastPollUtc ?? '');
  if (!Number.isFinite(polled) || now - polled > 60_000 || polled - now > 5_000) return undefined;
  return context;
}

/** A room may not replace a newer scoreboard state. Its explicitly timestamped last play is separate. */
export function usableContext(game: LiveGame, context: LiveGameContext | undefined, now: number): LiveGameContext | undefined {
  if (!freshContext(game, context, now)) return undefined;
  const shown = clock(game.detail);
  const { state } = context!;
  if (shown && (state.period < shown.period || state.period === shown.period && state.clockSec > shown.sec)) return undefined;
  if (!shown && (state.homeScore !== game.home.score || state.awayScore !== game.away.score)) return undefined;
  return context;
}

export function discoveryGames(games: readonly LiveGame[], contexts: readonly LiveGameContext[], fan: Pick<Fan, 'team'>, now = Date.now()): DiscoveryGame[] {
  return games.filter((g) => g.status === 'live').map((g): DiscoveryGame => {
    const context = freshContext(g, contexts.find((c) => c.gameId === g.gameId), now);
    const aligned = usableContext(g, context, now);
    const observed = aligned ? { ...g, home: { ...g.home, score: aligned.state.homeScore }, away: { ...g.away, score: aligned.state.awayScore }, detail: formatClock(aligned.state.period, aligned.state.clockSec) } : g;
    const card = liveCard(observed);
    const followed = !!fan.team && (fan.team === g.home.tricode || fan.team === g.away.tricode);
    const time = clock(observed.detail);
    const margin = Math.abs(observed.home.score - observed.away.score);
    const leader = observed.home.score > observed.away.score ? card.home.name : card.away.name;
    const overtime = !!time && time.period > 4;
    const late = !!time && time.period >= 4 && time.sec <= 240;
    const story = aligned?.historyReliable ? gameStory(aligned, aligned.plays, false) : undefined;
    const latest = context?.latestPlay;
    const playAge = time && latest ? gameElapsed(time.period, time.sec) - gameElapsed(latest.period, latest.clockSec) : undefined;
    const player = latest?.playerName && latest.personId && (playAge === undefined || playAge >= 0 && playAge <= 120) ? latest : undefined;
    const headline = story?.headline ?? (margin === 0 ? 'All tied up.' : late && margin <= 3 ? 'One possession can change this.' : `${leader} lead by ${margin}.`);
    const label = followed ? 'Your team' : overtime ? 'Overtime' : late && margin <= 5 ? 'A close finish' : story ? 'Game pulse' : margin <= 3 ? 'A close game' : 'On the floor';
    const detail = story?.detail ?? (player ? `Last observed: ${player.description}` : undefined) ?? (overtime ? 'Extra basketball. The finish is still open.' : late && margin <= 5 ? 'Pick a side. Be here for the finish.' : followed ? 'Your team is playing. Bring your reaction.' : 'Follow the plays. Make a call. React together.');
    return { card, followed, headline, detail, label, rank: (followed ? 1000 : 0) + (overtime ? 100 : 0) + (late && margin <= 5 ? 80 : 0) + (story ? 40 : 0) + Math.max(0, 20 - margin), ...(context ? { context } : {}), ...(story ? { story } : {}), ...(player ? { player } : {}) };
  }).sort((a, b) => b.rank - a.rank || a.card.gameId.localeCompare(b.card.gameId));
}

/** Keep simulated participants visible as test activity, never social proof from real fans. */
export function activityLine(context: LiveGameContext | undefined): string | undefined {
  if (!context) return undefined;
  const people = Math.max(0, context.onlineCount - context.testOnlineCount);
  const takes = Math.max(0, context.publishedTakeCount - context.testPublishedTakeCount);
  const parts = [];
  if (people) parts.push(`${people} ${people === 1 ? 'fan' : 'fans'} here`);
  if (takes) parts.push(`${takes} ${takes === 1 ? 'take' : 'takes'} posted`);
  if (context.testOnlineCount) parts.push(`${context.testOnlineCount} test ${context.testOnlineCount === 1 ? 'fan' : 'fans'}`);
  return parts.length ? parts.join(' · ') : undefined;
}
